import { SearchableSelect } from '@/components/common/SearchableSelect';
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von13Readmission } from '@/types/vonData.types';
import { api } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von13ReadmissionFormProps {
    patientId: number;
    onSaved?: () => void;
    readOnly?: boolean;
}

interface Hospital {
    hospital_id: number;
    hospital_name: string;
    city?: string;
}

export const Von13ReadmissionForm: React.FC<Von13ReadmissionFormProps> = ({
    patientId,
    onSaved,
    readOnly = false
}) => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [hasExistingData, setHasExistingData] = useState(false);
    const [hospitals, setHospitals] = useState<Hospital[]>([]);

    const [readmission, setReadmission] = useState(false);
    const [readmitDate, setReadmitDate] = useState<string>('');
    const [readmitReason, setReadmitReason] = useState<string>('');
    const [readmitHospId, setReadmitHospId] = useState<string>('');
    const [readmitDispo, setReadmitDispo] = useState<string>('');
    const [readmitDispoDate, setReadmitDispoDate] = useState<string>('');

    useEffect(() => {
        loadData();
        loadHospitals();
    }, [patientId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const data = await vonDataService.getReadmission(patientId);

            if (data) {
                setHasExistingData(true);
                setReadmission(Boolean(data.readmission));
                setReadmitDate(data.readmit_date || '');
                setReadmitReason(data.readmit_reason?.toString() || '');
                setReadmitHospId(data.readmit_hosp_id?.toString() || '');
                setReadmitDispo(data.readmit_dispo?.toString() || '');
                setReadmitDispoDate(data.readmit_dispo_date || '');
            }
        } catch (error) {
            toast({
                title: 'Eroare',
                description: 'Nu s-au putut încărca datele',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const loadHospitals = async () => {
        try {
            const response = await api.get<Hospital[]>('/api/reference/hospitals');
            setHospitals(response.data || []);
        } catch (error) {
            console.error('Error loading hospitals:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (readOnly) return;

        try {
            setSubmitting(true);

            const formData: Partial<Von13Readmission> = {
                patient_id: patientId,
                readmission: readmission,
                readmit_date: readmitDate || undefined,
                readmit_reason: readmitReason ? parseInt(readmitReason) : undefined,
                readmit_hosp_id: readmitHospId ? parseInt(readmitHospId) : undefined,
                readmit_dispo: readmitDispo ? parseInt(readmitDispo) : undefined,
                readmit_dispo_date: readmitDispoDate || undefined
            };

            if (hasExistingData) {
                await vonDataService.updateReadmission(patientId, formData);
            } else {
                await vonDataService.createReadmission(patientId, formData);
                setHasExistingData(true);
            }

            toast({
                title: 'Succes',
                description: 'Datele au fost salvate cu succes'
            });

            if (onSaved) {
                onSaved();
            }
        } catch (error) {
            toast({
                title: 'Eroare',
                description: 'Nu s-au putut salva datele',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Secțiunea 13: Reinternare</CardTitle>
                <CardDescription>Date despre reinternarea după externare</CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Readmission status */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium">Status reinternare</h3>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="readmission"
                                checked={readmission}
                                onCheckedChange={(checked) => setReadmission(checked === true)}
                                disabled={readOnly}
                            />
                            <Label htmlFor="readmission" className="text-sm font-normal cursor-pointer">
                                Reinternare după externare
                            </Label>
                        </div>

                        {readmission && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="readmitDate">Data reinternării</Label>
                                    <Input
                                        id="readmitDate"
                                        type="date"
                                        value={readmitDate}
                                        onChange={(e) => setReadmitDate(e.target.value)}
                                        disabled={readOnly}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="readmitReason">Motiv reinternare</Label>
                                    <Select value={readmitReason} onValueChange={setReadmitReason} disabled={readOnly}>
                                        <SelectTrigger id="readmitReason">
                                            <SelectValue placeholder="Selectează..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="1">Respirator</SelectItem>
                                            <SelectItem value="2">Cardiac</SelectItem>
                                            <SelectItem value="3">Gastrointestinal</SelectItem>
                                            <SelectItem value="4">Neurologic</SelectItem>
                                            <SelectItem value="5">Infecție</SelectItem>
                                            <SelectItem value="6">Chirurgical</SelectItem>
                                            <SelectItem value="7">Nutriție</SelectItem>
                                            <SelectItem value="8">ROP</SelectItem>
                                            <SelectItem value="9">Monitorizare</SelectItem>
                                            <SelectItem value="10">Altele</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="readmitHospId">Reinternare din spitalul</Label>
                                    <SearchableSelect
                                        id="readmitHospId"
                                        options={hospitals.map((hospital) => ({
                                            value: hospital.hospital_id.toString(),
                                            label: hospital.hospital_name,
                                        }))}
                                        value={readmitHospId}
                                        onChange={setReadmitHospId}
                                        placeholder="Selectează spital..."
                                        searchPlaceholder="Caută spital sau maternitate..."
                                        disabled={readOnly}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="readmitDispo">Dispoziție după reinternare</Label>
                                    <Select value={readmitDispo} onValueChange={setReadmitDispo} disabled={readOnly}>
                                        <SelectTrigger id="readmitDispo">
                                            <SelectValue placeholder="Selectează..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="1">Reexternat acasă</SelectItem>
                                            <SelectItem value="2">Decedat</SelectItem>
                                            <SelectItem value="3">Încă internat la 1 an</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="readmitDispoDate">Data dispoziție</Label>
                                    <Input
                                        id="readmitDispoDate"
                                        type="date"
                                        value={readmitDispoDate}
                                        onChange={(e) => setReadmitDispoDate(e.target.value)}
                                        disabled={readOnly}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {!readOnly && (
                        <div className="flex justify-end">
                            <Button type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Se salvează...
                                    </>
                                ) : (
                                    <>
                                        <Save className="mr-2 h-4 w-4" />
                                        Salvează
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </form>
            </CardContent>
        </Card>
    );
};
